// src/pages/SignIn.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../Utils/authSlice"; // adjust import path if needed
import cheflylandingpage from "../assets/jagodslandingpage.png";
import { useAuth } from "../Hooks/useAuth";
import { USER_ROLE, API_BASE_URL } from "../helpers/Constant";
import { getDeviceIdSync } from "../Utils/deviceId";
import Toast from "../components/Toast";

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated, clearAuthError } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [loginResponse, setLoginResponse] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [toast, setToast] = useState(null);
  const deviceId = getDeviceIdSync();

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/profile");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail");
    const rememberMeChecked = localStorage.getItem("rememberMe");
    
    if (savedEmail && rememberMeChecked === "true") {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Helper to process login response and update state
  const processLoginResponse = (data) => {
    // Extract token and user from response
    const userData = data.data || data;
    const token = userData.token || data.token;
    if (!token) {
      throw new Error("No token received from server.");
    }

    // Store in localStorage
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", token);
    if (userData.refresh_token) {
      localStorage.setItem("refresh_token", userData.refresh_token);
    }
    if (userData.expires_in) {
      localStorage.setItem("expires_in", userData.expires_in);
    }
    if (userData.restaurant) {
      localStorage.setItem("restaurant", JSON.stringify(userData.restaurant));
    }

    // Update Redux auth state
    dispatch(loginSuccess({ user: userData, token }));

    // Handle "Remember me"
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem("savedEmail", email);
    } else {
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("savedEmail");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const errors = {};
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password,
          role: USER_ROLE.CUSTOMER
        })
      });

      const data = await response.json();
      console.log("Login response:", data);

      // Handle 422 validation errors
      if (response.status === 422) {
        if (data.message && typeof data.message === 'object') {
          const errors = {};
          Object.keys(data.message).forEach((key) => {
            const errorMessage = Array.isArray(data.message[key]) 
              ? data.message[key][0] 
              : data.message[key];
            
            const fieldMap = {
              'email': 'email',
              'password': 'password',
            };
            
            const frontendField = fieldMap[key] || key;
            errors[frontendField] = errorMessage;
          });
          
          setFieldErrors(errors);
          const firstError = Object.values(errors)[0];
          if (firstError) {
            showToast(firstError, 'error');
          }
        } else if (data.message && typeof data.message === 'string') {
          showToast(data.message, 'error');
        } else {
          showToast('Validation failed. Please check your inputs.', 'error');
        }
        return;
      }

      // Check if OTP is required (if the API ever returns this flag)
      if (data.requires_otp) {
        setLoginResponse(data);
        setShowOTPModal(true);
        setOtp("");
        setOtpError("");
        showToast("OTP sent to your registered email and phone.", 'info');
        return;
      }

      // Successful login (status_code === 200)
      if (data.status_code === 200) {
        try {
          processLoginResponse(data);
          showToast("Login successful! Redirecting...", 'success');
          setTimeout(() => {
            navigate("/profile");
          }, 500);
        } catch (tokenError) {
          console.error("Token extraction error:", tokenError);
          const errorMsg = tokenError.message || "Login failed: no token received.";
          setFieldErrors({ email: errorMsg });
          showToast(errorMsg, 'error');
        }
      } else {
        const errorMsg = data.message || "Login failed. Please check your credentials and try again.";
        setFieldErrors({ email: errorMsg });
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      console.error("LOGIN ERROR => ", error);
      const errorMsg = "Network error. Please check your connection and try again.";
      setFieldErrors({ email: errorMsg });
      showToast(errorMsg, 'error');
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsVerifying(true);
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_or_phone: email,
          otp: otp,
          purpose: "device_verification",
          role: USER_ROLE.CUSTOMER
        })
      });

      const data = await response.json();
      console.log("OTP verification response:", data);

      if (response.status === 422) {
        if (data.message && typeof data.message === 'object') {
          const errorMsg = Object.values(data.message)[0];
          setOtpError(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg);
        } else {
          setOtpError(data.message || "Invalid OTP. Please try again.");
        }
        return;
      }

      if (data.status_code === 200) {
        setShowOTPModal(false);
        try {
          // The OTP verification response might have the same structure as login
          processLoginResponse(data);
          showToast("OTP verified successfully!", 'success');
          setTimeout(() => {
            navigate("/profile");
          }, 500);
        } catch (tokenError) {
          console.error("OTP token extraction error:", tokenError);
          setOtpError(tokenError.message || "Verification successful but no token received.");
          showToast(tokenError.message || "Verification successful but no token received.", 'error');
        }
      } else {
        if (data.message && data.message.includes("expired")) {
          setOtpError("OTP has expired. Please request a new one.");
          showToast("OTP expired. Please request a new one.", 'error');
        } else {
          const errorMsg = data.message || "Invalid OTP. Please try again.";
          setOtpError(errorMsg);
          showToast(errorMsg, 'error');
        }
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      const errorMsg = "Network error. Please check your connection and try again.";
      setOtpError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'X-Device-ID': deviceId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password,
          role: USER_ROLE.CUSTOMER
        })
      });

      const data = await response.json();

      if (response.status === 422) {
        const errorMsg = data.message || "Invalid credentials. Please try again.";
        setOtpError(errorMsg);
        showToast(errorMsg, 'error');
        return;
      }

      if (data.requires_otp) {
        setLoginResponse(data);
        setOtp("");
        showToast("OTP has been resent to your registered email and phone.", 'success');
      } else if (data.status_code === 200) {
        setShowOTPModal(false);
        try {
          processLoginResponse(data);
          showToast("Login successful!", 'success');
          setTimeout(() => {
            navigate("/profile");
          }, 500);
        } catch (tokenError) {
          setOtpError(tokenError.message || "No token received.");
          showToast(tokenError.message || "No token received.", 'error');
        }
      } else {
        const errorMsg = data.message || "Failed to resend OTP. Please try again.";
        setOtpError(errorMsg);
        showToast(errorMsg, 'error');
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      const errorMsg = "Network error. Please check your connection and try again.";
      setOtpError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setIsResending(false);
    }
  };

  const handleClearError = () => {
    clearAuthError();
    setFieldErrors({});
  };

  // OTP Modal Component (unchanged)
  const OTPModal = () => {
    if (!showOTPModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-yellow-100 p-3">
                <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Security Verification</h3>
            <p className="mt-2 text-sm text-gray-600">
              {loginResponse?.risk?.reasons?.length > 0 && (
                <span className="block text-yellow-600 font-medium mb-2">
                  ⚠️ {loginResponse.risk.reasons[0]}
                </span>
              )}
              <span className="block">
                We've sent a verification code to your registered email and phone. 
                Please enter the 6-digit OTP to continue.
              </span>
              {loginResponse?.risk?.risk_score && (
                <span className="block mt-1 text-xs text-gray-500">
                  Risk Score: {loginResponse.risk.risk_score}
                </span>
              )}
            </p>
          </div>

          <div className="mt-6">
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Enter OTP
            </label>
            <div className="flex h-[60px] items-center overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 focus-within:border-yellow-500 focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(255,193,7,0.12)]">
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 6) {
                    setOtp(value);
                    if (otpError) setOtpError("");
                  }
                }}
                placeholder="Enter 6-digit code"
                className="h-full w-full bg-transparent px-5 text-center text-lg font-semibold tracking-widest outline-none placeholder:text-gray-400"
                autoFocus
              />
            </div>
            {otpError && (
              <p className="mt-2 text-sm text-red-500">{otpError}</p>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleVerifyOTP}
              disabled={isVerifying}
              className="h-[52px] w-full rounded-2xl bg-yellow-500 text-base font-bold text-white shadow-[0_10px_30px_rgba(255,193,7,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                "Verify & Continue"
              )}
            </button>
            
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isResending}
                className="text-yellow-600 hover:underline font-medium disabled:opacity-50"
              >
                {isResending ? "Sending..." : "Resend OTP"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowOTPModal(false);
                  setOtp("");
                  setOtpError("");
                }}
                className="text-gray-500 hover:text-gray-700 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="min-h-screen overflow-hidden bg-[#fff7f2]">
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast} 
        />
      )}

      <div className="mx-auto grid min-h-screen max-w-[1800px] grid-cols-1 lg:grid-cols-2">
        {/* Left Side - Form */}
        <div className="flex min-h-screen items-center justify-center bg-[#fff] px-6 py-10 sm:px-10 lg:px-16">
          <div className="w-full max-w-[540px]">
            <h2 className="text-5xl font-black leading-[1.1] tracking-tight text-[#111827] sm:text-6xl">
              Welcome Back
            </h2>

            <p className="mt-5 max-w-md text-lg leading-relaxed text-[#6b7280]">
              Login to continue ordering your favourite meals with fast delivery and exclusive offers.
            </p>

            {error && (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-medium text-red-500">{error}</p>
                <button
                  onClick={handleClearError}
                  className="mt-2 text-xs font-medium text-red-400 hover:text-red-600"
                >
                  Dismiss
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-10 space-y-6">
              <div>
                <label className="mb-3 block text-sm font-bold uppercase tracking-wide text-[#4b5563]">
                  Email Address
                </label>
                <div className={`group flex h-[68px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                  fieldErrors.email ? "border-red-500" : "border-[#e5e7eb]"
                }`}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error || fieldErrors.email) {
                        clearAuthError();
                        setFieldErrors({});
                      }
                    }}
                    className="h-full w-full bg-transparent px-4 text-lg text-[#111827] outline-none placeholder:text-[#9ca3af]"
                  />
                </div>
                {fieldErrors.email && (
                  <p className="mt-2 text-sm text-red-500">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <label className="text-sm font-bold uppercase tracking-wide text-[#4b5563]">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-sm font-semibold text-[#6c33cd] transition hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className={`group flex h-[68px] items-center overflow-hidden rounded-2xl border bg-[#f9fafb] transition-all duration-300 focus-within:border-[#6c33cd] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(108,51,205,0.12)] ${
                  fieldErrors.password ? "border-red-500" : "border-[#e5e7eb]"
                }`}>
                  <div className="pl-5 text-[#9ca3af] group-focus-within:text-[#6c33cd]">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error || fieldErrors.password) {
                        clearAuthError();
                        setFieldErrors({});
                      }
                    }}
                    className="h-full w-full bg-transparent px-4 text-lg text-[#111827] outline-none placeholder:text-[#9ca3af]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="pr-5 text-[#9ca3af] hover:text-[#6c33cd] transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-2 text-sm text-red-500">{fieldErrors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300 accent-[#6c33cd]"
                  />
                  <span className="text-base text-[#6b7280]">Remember me</span>
                </label>
                <span className="text-sm font-medium text-[#9ca3af]">Secure Login</span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="h-[70px] w-full rounded-2xl bg-[#6c33cd] text-lg font-bold text-white shadow-[0_15px_40px_rgba(108,51,205,0.35)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#5b21b6] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="h-6 w-6 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging In...
                  </span>
                ) : (
                  "Login"
                )}
              </button>

              <p className="pt-4 text-center text-base text-[#6b7280]">
                Don't have an account?{" "}
                <span
                  onClick={() => {
                    clearAuthError();
                    setFieldErrors({});
                    navigate("/signup");
                  }}
                  className="cursor-pointer font-bold text-[#6c33cd] hover:underline"
                >
                  Create Account
                </span>
              </p>
            </form>
          </div>
        </div>

        {/* Right Side - Image */}
        <div className="hidden lg:flex relative h-screen overflow-hidden bg-[#fff7f2]">
          <img
            src={cheflylandingpage}
            alt="Food Delivery"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* OTP Modal */}
      <OTPModal />
    </section>
  );
};

export default SignIn;